import React, { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import fi from "date-fns/locale/fi";
import sv from "date-fns/locale/sv";
import enGB from "date-fns/locale/en-GB";
import "../../../cssSource/less/calendar.less";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Base } from "../../framework/common/base";
import { AppUtils } from "../../models/common/appUtils";
import { useSelectDailyAllowancesByDate, useSelectPaidWorkHoursAmountByDate } from "../../hooks/worktime/useSelectWorkHours";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import { fetchCalendarDays } from "../../framework/redux/slices/calendarSlice";
import dayjs from "dayjs";
import { useSelectCalendarDay } from "../../hooks/calendar/useCalendar";
import { fetchDayBookings } from "../../framework/redux/slices/dayBookingSlice";
import { handleApiError } from "../../services/baseService";
import { useSelectDaybookingsByDateRange } from "../../hooks/worktime/useSelectDayBookings";
import { DayBooking } from "../../models/dayBooking/dayBooking";
import { useSelectPlannedWorkShiftsDurationByDate, useSelectPlannedWorkShiftsByDate } from "../../hooks/worktime/useSelectPlannedWorkShifts";
import { useShowWorkShiftEndTimesInMobile } from "../../hooks/framework/useShowWorkShiftEndTimesInMobile";
import CircleIcon from "@mui/icons-material/Circle";
import { commonBoxStyle } from "../layout/mainLayout";
import { useTranslation } from "react-i18next";
import { SvgIconOwnProps } from "@mui/material";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import { OptionWorkShiftPlanningEnabled, OptionRoutesEnabled } from "../../models/common/consts";
import { cultureCodeEnglish, cultureCodeFinnish, cultureCodeSwedish } from "../../i18n/i18n";

interface Props {
    date: string;
    setDate: (date: string) => void;
}

const getDateFnsLocale = (culture: string) => {
    switch (culture) {
        case cultureCodeFinnish:
            return fi;
        case cultureCodeSwedish:
            return sv;
        case cultureCodeEnglish:
        default:
            return enGB;
    }
};

export const WorklogCalendar = ({ date, setDate }: Props) => {
    const [selected, setSelected] = useState(new Date(date));
    const { t, i18n } = useTranslation();

    const datePickerLocale = getDateFnsLocale(i18n.language);

    const [plannedShiftsEnabled] = useAppOptionValueEquals(
        OptionWorkShiftPlanningEnabled,
        true
    );

    const [routesEnabled] = useAppOptionValueEquals(
        OptionRoutesEnabled,
        true
    );

    const showWorkShiftEndTimes = useShowWorkShiftEndTimesInMobile();

    const startDate = dayjs(date).startOf("month").startOf("week").toISOString();
    const endDate = dayjs(date).endOf("month").endOf("week").toISOString();

    const plannedShifts = useSelectPlannedWorkShiftsByDate(startDate, endDate);
    const hasRoutesInPeriod = plannedShifts?.some(shift => shift.routeName);

    useEffect(() => {
        setSelected(new Date(date));
    }, [date]);

    useAsyncThunkAction(
        () =>
            fetchCalendarDays({
                startDate: startDate,
                endDate: endDate
            }),
        { onError: () => null },
        [date]
    );

    const dayBookings = useSelectDaybookingsByDateRange(startDate, endDate);

    useAsyncThunkAction(
        () =>
            fetchDayBookings({
                startDate: startDate,
                endDate: endDate
            }),
        {
            onError: handleApiError,
        },
        [date]
    );

    const handleMonthChange = (date: Date) => {
        setDate(Base.dateToDateJSON(date));
    };

    const renderDayContents = (day: number, date: Date) => {
        return <DayContents
            day={day}
            date={date}
            dayBookingItems={dayBookings}
            routesEnabled={routesEnabled}
               />;
    };

    const getPlannedHoursLabel = () => {
        if (showWorkShiftEndTimes) {
            return t("workTime.calendar.plannedHours");
        } else {
            return routesEnabled && hasRoutesInPeriod
                ? t("workTime.calendar.route")
                : t("workTime.calendar.shiftStart");
        }
    };

    return (
        <Stack>
            <Box className="worktime-datepicker-container">
                <DatePicker
                    calendarClassName="worktime-datepicker"
                    dateFormat="dd.MM.yyyy"
                    locale={datePickerLocale}
                    selected={selected}
                    inline
                    renderDayContents={renderDayContents}
                    onMonthChange={handleMonthChange}
                    onChange={(value: Date) => {
                        setDate(Base.dateToDateJSON(value));
                    }}
                    weekLabel={" "}
                    showWeekNumbers
                />
            </Box>
            <Box sx={commonBoxStyle}>
                <Stack
                    direction="row"
                    spacing={{ xs: 1, sm: 2, md: 3 }}
                    flexWrap="wrap"
                    justifyContent="center"
                    alignItems="center"
                >
                    {plannedShiftsEnabled && (
                        <CalendarLabelItem
                            color="blue"
                            label={getPlannedHoursLabel()}
                        />
                    )}
                    <CalendarLabelItem
                        color="green"
                        label={showWorkShiftEndTimes ? t("workTime.calendar.actual") : t("workTime.calendar.actualHours")}
                    />
                    <CalendarLabelItem
                        color="orange"
                        label={t("workTime.calendar.dayBookings")}
                    />
                </Stack>
            </Box>
        </Stack>
    );
};

const DayContents = (
    { day, date, dayBookingItems, routesEnabled } :
        { day: number; date: Date; dayBookingItems?: Array<DayBooking>; routesEnabled: boolean }
) => {
    const dateString = Base.dateToDateJSON(date);
    const hours = useSelectPaidWorkHoursAmountByDate(dateString, dateString);
    const shiftDuration = useSelectPlannedWorkShiftsDurationByDate(dateString, dateString);
    const plannedShifts = useSelectPlannedWorkShiftsByDate(dateString, dateString);
    const calendarDay = useSelectCalendarDay(dateString);
    const showWorkShiftEndTimes = useShowWorkShiftEndTimesInMobile();
    const { t } = useTranslation();

    const calcAllowances = useSelectDailyAllowancesByDate(
        dateString,
        dateString,
        true
    );

    const hasDayBookings = dayBookingItems?.some(
        (item) => item.day <= dateString && item.endDay >= dateString) ||
        calcAllowances?.some((item) => item.dateStr <= dateString && item.dateStr >= dateString
        );

    const getPlannedContent = () => {
        if (shiftDuration <= 0) return null;
        if (showWorkShiftEndTimes) {
            return AppUtils.getDurationStrByDurationMinShort(shiftDuration);
        } else {
            const earliestShift = plannedShifts
                ?.filter(shift => shift?.startTime)
                ?.sort((a, b) => new Date(a?.startTime).getTime() - new Date(b?.startTime).getTime())?.[0];

            if (routesEnabled) {
                // When routes are enabled, only show route names
                if (earliestShift?.routeName) {
                    return earliestShift.routeName.length > 6
                        ? earliestShift.routeName.substring(0, 6) + "..."
                        : earliestShift.routeName;
                }
                return t("workTime.calendar.noRoute");
            } else {
                // When routes are disabled, show earliest shift start time
                if (earliestShift?.startTime) {
                    const timeStr = Base.dateToTimeStr(earliestShift?.startTime);
                    return timeStr?.replace(":", ".");
                }
            }
            return AppUtils.getDurationStrByDurationMinShort(shiftDuration);
        }
    };

    const plannedContent = getPlannedContent();

    return (
        <>
            <span
                style={{
                    color: calendarDay?.isHoliday ? "red" : "inherit",
                    lineHeight: "1.5rem",
                }}
            >
                <div className={hasDayBookings ? "has-daybookings" : null}>
                    {day}
                </div>
                {plannedContent && (
                    <p className={`worktime-datepicker-hours planned${!showWorkShiftEndTimes ? " compact" : ""}`}>
                        {plannedContent}
                    </p>
                )}
                {hours > 0 ? (
                    <p className="worktime-datepicker-hours">
                        {AppUtils.getDurationStrByDurationMinShort(hours)}
                    </p>
                ) : null}
            </span>
        </>
    );
};

interface CalendarLabelItemProps {
    color: SvgIconOwnProps["color"];
    label: string;
}

const CalendarLabelItem = ({ color, label }: CalendarLabelItemProps) => {
    return (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, flex: "0 1 auto" }}>
            <CircleIcon color={color} fontSize="small" />
            <Typography
                variant="body2"
                sx={{
                    color: `${color}.main`,
                    fontSize: { xs: "0.65rem", sm: "0.75rem" },
                    fontWeight: 400,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                    maxWidth: { xs: "80px", sm: "120px", md: "none" }
                }}
            >
                {label}
            </Typography>
        </Stack>
    );
};
